import React, { useState, useEffect, useMemo } from 'react';
import CharacterInput from '../components/CharacterInput';
import CharactersList from '../components/CharactersList';
import WordsList, { WordItem } from '../components/WordsList';
import WordCard from '../components/WordCard';
import { Word, WordStatus, fetchCombinations, updateWordStatus } from '../utils/api';
import '../styles/Combinations.scss';
import logger from '../utils/logger';

const fetchSuggestedCharacters = (): Promise<string[]> => {
  return Promise.resolve(['你', '好', '我', '是', '中', '国', '人', '学', '习', '汉', '字']);
};

const Combinations = () => {
  const [characters, setCharacters] = useState<string[]>([]);
  const [suggestedCharacters, setSuggestedCharacters] = useState<string[]>([]);
  const [isHowItWorksVisible, setIsHowItWorksVisible] = useState(false);
  const [isResultVisible, setIsResultVisible] = useState(false);
  const [combinationResults, setCombinationResults] = useState<Array<Word & { characters: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedWord, setSelectedWord] = useState<Word | null>(null);
  const [highlightedWords, setHighlightedWords] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchSuggestedCharacters().then(setSuggestedCharacters);
  }, []);

  const filteredSuggestedCharacters = useMemo(() => {
    return suggestedCharacters.filter(char => !characters.includes(char));
  }, [suggestedCharacters, characters]);

  const handleCharacterSubmit = (character: string) => {
    if (!characters.includes(character)) {
      setCharacters([character, ...characters]);
    }
  };

  const handleSuggestionClick = (character: string) => {
    if (!characters.includes(character)) {
      setCharacters([character, ...characters]);
    }
  };

  const handleCharacterRemove = (character: string) => {
    setCharacters(characters.filter(char => char !== character));
  };

  const handleCombine = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchCombinations(characters);
      if (response.success && response.data) {
        logger.log('Получены комбинации:', response.data);
        const formattedResults = response.data.map(item => ({
          ...item,
          characters: item.word 
        }));
        setCombinationResults(formattedResults);
        setIsResultVisible(true);
      } else {
        setError(response.error || 'Не удалось получить комбинации');
        logger.error('Ошибка при получении комбинаций:', response.error);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(`Произошла ошибка: ${errorMsg}`);
      logger.error('Исключение при получении комбинаций:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSelection = () => {
    setIsResultVisible(false);
    setCombinationResults([]);
  };

  const toggleHowItWorks = () => {
    setIsHowItWorksVisible(!isHowItWorksVisible);
  };

  const handleWordClick = (word: WordItem) => {
    logger.log('Выбрано слово в комбинациях:', word);
    const wordData: Word = {
      word: word.characters,
      pinyin: word.pinyin || '',
      translation: word.translation || '',
      priority: word.priority || 0,
      example: word.example
    };
    setSelectedWord(wordData);
  };

  const handleWordDoubleClick = async (word: WordItem) => {
    logger.log('Двойной клик по слову в комбинациях:', word);
    
    // Получаем данные слова
    const wordData: Word = {
      word: word.characters,
      pinyin: word.pinyin || '',
      translation: word.translation || '',
      priority: word.priority || 0,
      example: word.example
    };
    
    try {
      // Обновляем статус слова на "Учу" (IN_PROGRESS)
      const response = await updateWordStatus(wordData.word, 'IN_PROGRESS');
      
      if (response.success) {
        logger.log(`Статус слова "${wordData.word}" успешно изменен на IN_PROGRESS после двойного клика`);
        
        // Добавляем слово в список выделенных
        setHighlightedWords(prev => {
          const newSet = new Set(prev);
          newSet.add(word.characters);
          return newSet;
        });
      } else {
        logger.error(`Ошибка при изменении статуса слова: ${response.error}`);
      }
    } catch (error) {
      logger.error('Исключение при изменении статуса слова:', error);
    }
  };

  const handleCloseWordCard = () => {
    setSelectedWord(null);
  };

  const handleStatusChange = (status: WordStatus) => {
    logger.log(`Статус слова изменен на: ${status}`);
    // После изменения статуса просто закрываем карточку
    setSelectedWord(null);
    
    // Если статус "Учу", добавляем слово в выделенные
    if (status === 'IN_PROGRESS' && selectedWord) {
      setHighlightedWords(prev => {
        const newSet = new Set(prev);
        newSet.add(selectedWord.word);
        return newSet;
      });
    }
  };

  return (
    <div className="combinations-page">
      <h1>
        Комбинации
        <div className="how-it-works">
          <button className="how-it-works-button" onClick={toggleHowItWorks}>
            ?
          </button>
          <div 
            className={`how-it-works-overlay ${isHowItWorksVisible ? 'visible' : ''}`}
            onClick={toggleHowItWorks}
          >
            <div 
              className="how-it-works-text"
              onClick={(e) => e.stopPropagation()}
            >
              <p>Знаете иероглифы? Отлично! Давайте создавать из них новые слова.</p>
              <p>Просто добавьте знакомые иероглифы, а мы покажем все возможные комбинации.</p>
              <p>Так без заучивания иероглифов можно учить новые слова. Круто, правда?</p>
            </div>
          </div>
        </div>
      </h1>
      {!isResultVisible ? (
        <div className="combinations-content">
          <CharactersList
            characters={filteredSuggestedCharacters}
            onCharacterClick={handleSuggestionClick}
            className="suggested-characters"
            itemClassName="character-suggestion"
            title="Популярные иероглифы:"
          />
          <CharacterInput onCharacterSubmit={handleCharacterSubmit} />
          <div className="selected-section">
            <CharactersList
              characters={characters}
              onCharacterClick={handleCharacterRemove}
              className="selected-characters"
              itemClassName="character-selected"
            />
            {characters.length > 0 && (
              <button 
                className="combine-button" 
                onClick={handleCombine}
                disabled={isLoading}
              >
                {isLoading ? 'Загрузка...' : 'Комбинировать'}
              </button>
            )}
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
      ) : (
        <div className="combinations-results">
          {error ? (
            <div className="error-message">{error}</div>
          ) : isLoading ? (
            <div className="loading-message">Загрузка комбинаций...</div>
          ) : combinationResults.length > 0 ? (
            <WordsList 
              words={combinationResults} 
              className="results-list"
              itemClassName="result-item"
              onWordClick={handleWordClick}
              onWordDoubleClick={handleWordDoubleClick}
              highlightedWords={highlightedWords}
            />
          ) : (
            <div className="no-results-message">
              Комбинации не найдены для выбранных иероглифов
            </div>
          )}
          <button className="back-button" onClick={handleBackToSelection}>
            К выбору
          </button>
        </div>
      )}

      {selectedWord && (
        <WordCard 
          word={selectedWord}
          onClose={handleCloseWordCard}
          showStatusButtons={true}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

export default Combinations;